/**
 * MedicineSuggestionPanel.jsx
 *
 * TAB 1 — 🩺 Symptoms
 *   Watches COMPLAINTS box automatically.
 *   "kasa" → matches "vataja kasa", "kaphaja kasa" (substring)
 *   Results ranked by how many symptom tokens match.
 *
 * TAB 2 — 🌿 Dravyas
 *   Watches ADVICE box automatically (where doctor writes ingredients/formulas).
 *   Each word/token is matched against medicine dravyas array.
 *   Medicine with MOST matching dravyas ranks first.
 *   Expanded view shows ALL indications as full readable text so doctor can
 *   decide whether to pick that formula.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import api from "../services/api";

function htmlToText(html = "") {
  if (!html) return "";
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ").trim();
}

function useDebounced(value, delay) {
  const [dv, setDv] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDv(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return dv;
}

function copyText(t) { try { navigator.clipboard.writeText(t); } catch {} }

export default function MedicineSuggestionPanel({ complaintsHtml, adviceHtml, doctorUser, dravyaQueryText, bubbleOffsetY = 0 }) {
  const [tab,         setTab]         = useState("symptoms");
  const [suggestions, setSuggestions] = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [expanded,    setExpanded]    = useState(null);
  const [copied,      setCopied]      = useState(null);
  const [visible,     setVisible]     = useState(true);
  /* Collapsed-by-default floating bubble — expands on click, collapses on
     click outside. Never obstructs typing while collapsed. */
  const [open,        setOpen]        = useState(false);
  const [minimised,   setMinimised]   = useState(false);
  const [pos,         setPos]         = useState(() => {
    try {
      const saved = localStorage.getItem("msp_pos");
      if (saved) return JSON.parse(saved);
    } catch {}
    return { x: null, y: null };
  });

  const panelRef   = useRef(null);
  const dragRef    = useRef({ dragging: false });
  const dragRafRef = useRef(null);

  /* ── Queries: symptoms reads complaints, dravyas reads the ACTIVE
     medicine row's own text (falls back to full advice text if no
     row is focused) — so each medicine row's ingredient search
     starts fresh instead of sharing one running query. ── */
  const symptomsText = useDebounced(htmlToText(complaintsHtml), 650);
  const dravyaSource  = (dravyaQueryText && dravyaQueryText.trim())
    ? dravyaQueryText
    : htmlToText(adviceHtml);
  const dravyasText  = useDebounced(dravyaSource, 400);

  const activeQuery = tab === "symptoms" ? symptomsText : dravyasText;
  const activeMode  = tab === "symptoms" ? "symptoms"   : "dravyas";

  /* ── Fetch ── */
  useEffect(() => {
    if (!open) return; // no need to query while collapsed to a bubble
    if (!activeQuery || activeQuery.length < 2) { setSuggestions([]); return; }
    let cancelled = false;
    setLoading(true);
    api.get(`/medicines/suggest?q=${encodeURIComponent(activeQuery)}&mode=${activeMode}`)
      .then(r  => { if (!cancelled) { setSuggestions(r.data || []); } })
      .catch(() => { if (!cancelled) setSuggestions([]); })
      .finally(()=> { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeQuery, activeMode, open]);

  /* Auto-expand first result in dravya mode so indications are immediately visible */
  useEffect(() => {
    if (tab === "dravyas" && suggestions.length > 0) {
      setExpanded(suggestions[0]._id);
    } else {
      setExpanded(null);
    }
  }, [suggestions, tab]);

  const switchTab = (t) => { setTab(t); setSuggestions([]); setExpanded(null); };

  /* Panel stays open once expanded — like VS Code's sidebar or Chrome
     DevTools. It only closes via the header × button or by clicking
     the bubble again (see the header buttons below). Clicking
     elsewhere in the page — including into a medicine row — must NOT
     dismiss it, since the doctor needs to reference suggestions while
     typing elsewhere. */

  /* ── Drag ── */
  const onDrag = useCallback((e) => {
    if (!dragRef.current.dragging) return;
    if (dragRafRef.current) cancelAnimationFrame(dragRafRef.current);
    dragRafRef.current = requestAnimationFrame(() => {
      setPos({
        x: dragRef.current.startLeft + (e.clientX - dragRef.current.startX),
        y: dragRef.current.startTop  + (e.clientY - dragRef.current.startY),
      });
    });
  }, []);
  const endDrag = useCallback(() => {
    dragRef.current.dragging = false;
    window.removeEventListener("pointermove", onDrag);
    window.removeEventListener("pointerup",   endDrag);
    setPos(current => {
      if (current.x !== null) {
        try { localStorage.setItem("msp_pos", JSON.stringify(current)); } catch {}
      }
      return current;
    });
  }, [onDrag]);
  const startDrag = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = { dragging: true, startX: e.clientX, startY: e.clientY, startLeft: rect.left, startTop: rect.top };
    window.addEventListener("pointermove", onDrag);
    window.addEventListener("pointerup",   endDrag);
  }, [onDrag, endDrag]);
  useEffect(() => () => {
    window.removeEventListener("pointermove", onDrag);
    window.removeEventListener("pointerup",   endDrag);
    if (dragRafRef.current) cancelAnimationFrame(dragRafRef.current);
  }, [onDrag, endDrag]);

  const handleCopy = (name) => {
    copyText(name);
    setCopied(name);
    setTimeout(() => setCopied(null), 1800);
  };

  /* ── Guard ── */
  if (!doctorUser?.hasMedicineSuggestions) return null;
  if (!visible) return null;

  /* ── Collapsed bubble (default state) — never obstructs typing ── */
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Medicine Suggestions"
        style={{
          position: "fixed", zIndex: 10000,
          bottom: 24 + bubbleOffsetY, right: 24,
          width: 52, height: 52, borderRadius: "50%",
          background: "linear-gradient(135deg, #16A34A, #15803D)",
          border: "none", boxShadow: "0 8px 24px rgba(21,128,61,0.35)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 22, cursor: "pointer",
        }}
      >
        💊
      </button>
    );
  }

  const panelStyle = {
    position: "fixed", zIndex: 10000,
    width: minimised ? 250 : 370,
    ...(pos.x !== null ? { left: pos.x, top: pos.y } : { bottom: 24 + bubbleOffsetY, right: 24 }),
  };

  const hasSuggestions = suggestions.length > 0;

  return (
    <div ref={panelRef} style={panelStyle}>
      <div style={{
        background: "#fff", border: "1.5px solid #E2E8F0", borderRadius: 18,
        boxShadow: "0 12px 48px rgba(0,0,0,0.16), 0 2px 8px rgba(0,0,0,0.07)",
        overflow: "hidden", fontFamily: "'Inter', system-ui, sans-serif",
        transition: "width 0.2s ease",
      }}>

        {/* ── Header ── */}
        <div onPointerDown={startDrag} style={{ background: "linear-gradient(135deg, #0F4C81, #1565A8)", padding: "11px 14px", cursor: "grab", display: "flex", alignItems: "center", justifyContent: "space-between", userSelect: "none" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 15 }}>💊</span>
            <span style={{ color: "#fff", fontWeight: 700, fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase" }}>Medicine Suggest</span>
            {loading && <span style={pulseStyle} />}
          </div>
          <div style={{ display: "flex", gap: 5 }}>
            <button onPointerDown={e => e.stopPropagation()} onClick={() => setMinimised(m => !m)} style={hdrBtn}>{minimised ? "▲" : "−"}</button>
            <button onPointerDown={e => e.stopPropagation()} onClick={() => setOpen(false)} title="Collapse to bubble" style={hdrBtn}>×</button>
          </div>
        </div>

        {!minimised && (<>

          {/* ── Tabs ── */}
          <div style={{ display: "flex", borderBottom: "2px solid #F1F5F9", background: "#FAFBFD" }}>
            {[
              { key: "symptoms", icon: "🩺", label: "Symptoms",
                sub: "reads Complaints" },
              { key: "dravyas",  icon: "🌿", label: "Dravyas",
                sub: "reads Advice" },
            ].map(({ key, icon, label, sub }) => (
              <button key={key} onClick={() => switchTab(key)} style={{
                flex: 1, padding: "9px 4px 7px", border: "none", background: "none", cursor: "pointer",
                fontFamily: "'Inter', sans-serif", marginBottom: -2,
                borderBottom: tab === key ? "2px solid #0F4C81" : "2px solid transparent",
              }}>
                <div style={{ fontSize: 11.5, fontWeight: tab === key ? 800 : 500, color: tab === key ? "#0F4C81" : "#94A3B8" }}>
                  {icon} {label}
                </div>
                <div style={{ fontSize: 9, color: tab === key ? "#60A5FA" : "#CBD5E1", marginTop: 1 }}>{sub}</div>
              </button>
            ))}
          </div>

          {/* ── Source indicator ── */}
          <div style={{ padding: "7px 14px 0", fontSize: 10, color: "#94A3B8", display: "flex", alignItems: "center", gap: 5 }}>
            {tab === "symptoms" ? (
              symptomsText.length >= 2
                ? <><span style={{ color: "#22C55E" }}>●</span> Auto-reading Complaints: "{symptomsText.slice(0, 44)}{symptomsText.length > 44 ? "…" : ""}"</>
                : <><span style={{ color: "#E5E7EB" }}>●</span> Waiting for text in <strong style={{ color: "#64748B" }}>Complaints</strong>…</>
            ) : (
              dravyasText.length >= 2
                ? <><span style={{ color: "#22C55E" }}>●</span> Auto-reading Advice: "{dravyasText.slice(0, 44)}{dravyasText.length > 44 ? "…" : ""}"</>
                : <><span style={{ color: "#E5E7EB" }}>●</span> Write dravyas / formulas in <strong style={{ color: "#64748B" }}>Advice</strong>…</>
            )}
          </div>

          {/* ── Dravya mode: explanation pill ── */}
          {tab === "dravyas" && dravyasText.length >= 2 && (
            <div style={{ margin: "6px 12px 0", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, padding: "5px 10px", fontSize: 10, color: "#166534" }}>
              💡 Medicines with the <strong>most matching dravyas</strong> appear first · tap any card to see its full indications
            </div>
          )}

          {/* ── No results ── */}
          {!loading && activeQuery.length >= 2 && !hasSuggestions && (
            <div style={{ padding: "20px 16px", textAlign: "center", color: "#94A3B8", fontSize: 12 }}>No matching medicines found</div>
          )}

          {/* ── Results ── */}
          {hasSuggestions && (
            <div style={{ maxHeight: 460, overflowY: "auto", padding: "6px 0 4px" }}>
              {suggestions.map((med, i) => {
                const isOpen = expanded === med._id;
                return (
                  <div key={med._id} style={{ borderBottom: i < suggestions.length - 1 ? "1px solid #F1F5F9" : "none" }}>

                    {/* Card header row */}
                    <div
                      onClick={() => setExpanded(isOpen ? null : med._id)}
                      onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = "#F8FAFF"; }}
                      onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = "transparent"; }}
                      style={{ padding: "10px 14px", cursor: "pointer", display: "flex", alignItems: "flex-start", gap: 10, background: isOpen ? "#F8FAFF" : "transparent" }}
                    >
                      {/* Match score badge */}
                      <div style={{
                        minWidth: 28, height: 28, borderRadius: 8,
                        background: badgeBg(med.matchCount), color: badgeColor(med.matchCount),
                        fontSize: 12, fontWeight: 900,
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>
                        {med.matchCount}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Name + note icon */}
                        <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 3 }}>
                          <span style={{ fontWeight: 700, fontSize: 13, color: "#1E293B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 190 }}>
                            {med.name}
                          </span>
                          {med.note && <span title={med.note} style={{ fontSize: 13, color: "#F59E0B", cursor: "help", flexShrink: 0 }}>⚠</span>}
                        </div>

                        {/* Matched terms as pills */}
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                          {med.matchedTerms.slice(0, 4).map((t, ti) => (
                            <span key={ti} style={matchedPill}>{t}</span>
                          ))}
                          {med.matchedTerms.length > 4 && <span style={{ fontSize: 9, color: "#94A3B8" }}>+{med.matchedTerms.length - 4}</span>}
                        </div>

                        {/* Dravya mode: show total dravya count */}
                        {tab === "dravyas" && (
                          <div style={{ fontSize: 9.5, color: "#64748B", marginTop: 3 }}>
                            {med.matchCount} of {med.dravyas?.length || 0} dravyas matched
                          </div>
                        )}
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                        <button
                          onClick={e => { e.stopPropagation(); handleCopy(med.name); }}
                          style={{ background: copied === med.name ? "#DCFCE7" : "#F1F5F9", border: "none", borderRadius: 6, padding: "3px 8px", fontSize: 9.5, fontWeight: 700, cursor: "pointer", color: copied === med.name ? "#166534" : "#64748B" }}
                        >
                          {copied === med.name ? "✓ Copied" : "Copy"}
                        </button>
                        <span style={{ fontSize: 10, color: "#94A3B8" }}>{isOpen ? "▲" : "▼"}</span>
                      </div>
                    </div>

                    {/* ── Expanded detail ── */}
                    {isOpen && (
                      <div style={{ padding: "0 14px 14px", background: "#F8FAFF", borderTop: "1px solid #EFF6FF" }}>

                        {/* Clinical note */}
                        {med.note && (
                          <div style={{ margin: "10px 0 10px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 9, padding: "8px 12px" }}>
                            <div style={{ fontSize: 9, fontWeight: 800, color: "#92400E", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 3 }}>⚠ Clinical Note</div>
                            <div style={{ fontSize: 11, color: "#78350F", lineHeight: 1.65 }}>{med.note}</div>
                          </div>
                        )}

                        {/* Dravyas section */}
                        {med.dravyas?.length > 0 && (
                          <div style={{ marginBottom: 10, marginTop: 10 }}>
                            <div style={secLbl}>🌿 Dravyas / Ingredients ({med.dravyas.length})</div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 5 }}>
                              {med.dravyas.map((d, di) => {
                                const isMatched = tab === "dravyas" && med.matchedTerms.some(
                                  mt => d.toLowerCase().includes(mt.toLowerCase()) || mt.toLowerCase().includes(d.toLowerCase())
                                );
                                return (
                                  <span key={di} style={{
                                    ...greenPill,
                                    ...(isMatched ? { background: "#DCFCE7", border: "1px solid #86EFAC", fontWeight: 800, color: "#15803D" } : {}),
                                  }}>{d}</span>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* ── INDICATIONS — full list, readable text ── */}
                        {med.indications?.length > 0 && (
                          <div>
                            <div style={{ ...secLbl, marginBottom: 6 }}>
                              🩺 Indications ({med.indications.length})
                              {tab === "dravyas" && (
                                <span style={{ fontSize: 9, fontWeight: 400, color: "#94A3B8", marginLeft: 6, textTransform: "none", letterSpacing: 0 }}>
                                  — these are the diseases this medicine treats
                                </span>
                              )}
                            </div>
                            {/* Pill tags — all of them, no truncation */}
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                              {med.indications.map((ind, ii) => {
                                const isMatched = tab === "symptoms" && med.matchedTerms.includes(ind);
                                return (
                                  <span key={ii} style={{
                                    ...bluePill,
                                    ...(isMatched ? { background: "#DBEAFE", border: "1px solid #93C5FD", fontWeight: 800 } : {}),
                                  }}>{ind}</span>
                                );
                              })}
                            </div>
                            {/* Also show as flowing text for quick reading in dravya mode */}
                            {tab === "dravyas" && (
                              <div style={{ marginTop: 8, fontSize: 11, color: "#475569", lineHeight: 1.7, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, padding: "8px 10px" }}>
                                {med.indications.join(" · ")}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Footer ── */}
          <div style={{ padding: "6px 14px", borderTop: "1px solid #F1F5F9", background: "#FAFBFD", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 9.5, color: "#CBD5E1" }}>
              {hasSuggestions ? `${suggestions.length} result${suggestions.length !== 1 ? "s" : ""}` : ""}
            </span>
            <span style={{ fontSize: 9, color: "#CBD5E1", fontStyle: "italic" }}>
              {tab === "dravyas" ? "most dravya matches first" : "partial match · auto-updates"}
            </span>
          </div>

        </>)}
      </div>
      <style>{`@keyframes msp-pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.3;transform:scale(.55)}}`}</style>
    </div>
  );
}

/* ── Styles ── */
const pulseStyle = { width: 8, height: 8, borderRadius: "50%", background: "#7DD3FC", display: "inline-block", animation: "msp-pulse 0.9s ease-in-out infinite" };
const hdrBtn     = { background: "rgba(255,255,255,.18)", border: "none", color: "#fff", borderRadius: 6, width: 22, height: 22, fontSize: 14, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" };
const secLbl     = { fontSize: 9, fontWeight: 800, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.08em" };
const matchedPill= { fontSize: 9.5, background: "#DBEAFE", color: "#1D4ED8", border: "1px solid #93C5FD", borderRadius: 20, padding: "2px 7px", fontWeight: 700 };
const bluePill   = { fontSize: 10, background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE", borderRadius: 20, padding: "2px 8px" };
const greenPill  = { fontSize: 10, background: "#F0FDF4", color: "#166534", border: "1px solid #BBF7D0", borderRadius: 20, padding: "2px 8px" };
function badgeBg(n)    { return n >= 4 ? "#DCFCE7" : n >= 2 ? "#FEF9C3" : "#FEE2E2"; }
function badgeColor(n) { return n >= 4 ? "#166534" : n >= 2 ? "#854D0E" : "#991B1B"; }